import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgePercent, CheckCircle2, Sparkles, Clock, Wallet, Shield, Users,
  ChevronDown, Phone, ArrowRight, FileText, Home as HomeIcon,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useDocumentMeta } from '../lib/useDocumentMeta';

const FAQ_ITEMS = [
  {
    q: "Le nettoyage de vitres à domicile est-il éligible au crédit d'impôt 50 % ?",
    a: "Oui, le nettoyage de vitres effectué chez les particuliers à leur domicile est officiellement éligible aux services à la personne (SAP) et ouvre droit au crédit d'impôt de 50 %. Cela vaut pour vos fenêtres, baies vitrées, vérandas et encadrements. Concrètement, un devis à 180 € vous revient à 90 € après crédit d'impôt.",
  },
  {
    q: "Quels services SMT sont éligibles au crédit d'impôt ?",
    a: "Sont éligibles : le ménage à domicile, le nettoyage de vitres, le repassage, l'entretien de canapé/moquette, et plus largement toutes les prestations qui se déroulent à votre domicile principal ou secondaire en France. Les prestations en local commercial, bureau ou Airbnb-gestion ne sont pas éligibles (vous bénéficiez d'autres dispositifs en revanche).",
  },
  {
    q: "Comment SMT Services peut-il proposer le crédit d'impôt ?",
    a: "Nous sommes adhérents à la coopérative Unipros, qui regroupe plus de 7 000 professionnels du service à la personne en France. Grâce à Unipros, nous intervenons sous une certification SAP officielle, sans avoir besoin de créer une seconde structure. Vous bénéficiez donc du crédit d'impôt comme avec n'importe quel prestataire SAP agréé.",
  },
  {
    q: "Quel est le plafond annuel ?",
    a: "Le plafond est de 12 000 € de dépenses par foyer fiscal et par an, soit 6 000 € de crédit d'impôt maximum. Ce plafond est majoré de 1 500 € par enfant à charge et pour les personnes de plus de 65 ans (jusqu'à un plafond global de 15 000 €).",
  },
  {
    q: "Comment récupérer le crédit d'impôt concrètement ?",
    a: "Deux options : (1) en différé via votre déclaration d'impôts annuelle (nous vous remettons une attestation fiscale en janvier, à reporter case 7DB), ou (2) en avance immédiate grâce au dispositif Urssaf intégré par Unipros : vous ne payez que 50 % directement, l'Urssaf nous verse l'autre moitié. Inscription en 5 minutes lors de votre 1ʳᵉ facture.",
  },
  {
    q: "Y a-t-il un engagement ou un abonnement minimum ?",
    a: "Aucun. Vous pouvez bénéficier du crédit d'impôt sur une intervention ponctuelle comme sur un entretien régulier. Pas de contrat annuel obligatoire, pas de minimum de prestations.",
  },
  {
    q: "Les particuliers en location ou colocation peuvent-ils en bénéficier ?",
    a: "Oui. Le crédit d'impôt s'applique au domicile en tant que résidence principale ou secondaire, peu importe que vous soyez propriétaire ou locataire. Il suffit d'être contribuable français.",
  },
  {
    q: "Le crédit d'impôt fonctionne-t-il si je ne paye pas d'impôts ?",
    a: "Oui ! Depuis 2017, c'est un véritable « crédit d'impôt » (et plus une simple réduction) : même si vous n'êtes pas imposable, l'État vous reverse les 50 % par virement. Ce dispositif est donc accessible à tous les contribuables, imposables ou non.",
  },
];

const CreditImpotPage = () => {
  const [openIdx, setOpenIdx] = useState(0);

  useDocumentMeta({
    title: "Crédit d'impôt 50 % pour le nettoyage de vitres et ménage à domicile — SMT Services Royan",
    description:
      "Profitez du crédit d'impôt 50 % sur le nettoyage de vitres et le ménage à domicile à Royan et en Charente-Maritime. SMT Services est adhérent Unipros (SAP officiel) — devis gratuit, avance immédiate Urssaf possible.",
    path: '/credit-impot-50-particuliers',
    ogType: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((it) => ({
        '@type': 'Question',
        name: it.q,
        acceptedAnswer: { '@type': 'Answer', text: it.a },
      })),
    },
  });

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="relative bg-gradient-to-br from-emerald-50 via-blue-50 to-orange-50 pt-32 pb-16 overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-green-200 rounded-full blur-3xl opacity-30" />
          <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-200 rounded-full blur-3xl opacity-30" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-600 hover:text-[hsl(207,90%,44%)] text-sm mb-6"
              data-testid="back-to-home"
            >
              <ArrowRight className="w-4 h-4 rotate-180" /> Retour à l'accueil
            </Link>
            <div className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-1.5 rounded-full font-bold text-sm mb-5 shadow-md">
              <BadgePercent className="w-4 h-4" /> 50 % de crédit d'impôt
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight">
              Crédit d'impôt 50 % sur le nettoyage de vitres et le ménage
            </h1>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed max-w-3xl">
              Particuliers à <strong>Royan, Saint-Palais-sur-Mer, La Tremblade</strong> et toute la <strong>Charente-Maritime</strong> : profitez de <strong>50 % de crédit d'impôt</strong> sur le nettoyage de vos vitres et l'entretien de votre logement, grâce à notre adhésion à la coopérative <strong>Unipros</strong>.
            </p>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,44%)] text-white font-bold shadow-lg">
                <Link to="/#reservation" data-testid="hero-cta-devis">
                  <FileText className="w-4 h-4 mr-2" /> Demander un devis gratuit
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-2 font-semibold">
                <a href="tel:+33625684849" data-testid="hero-cta-call">
                  <Phone className="w-4 h-4 mr-2" /> 06 25 68 48 49
                </a>
              </Button>
            </div>
          </div>
        </section>

        {/* Explication simple */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Comment ça marche, simplement
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Le principe est aussi simple qu'avantageux. Voici les 3 étapes.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: FileText,
                title: '1. Vous réservez',
                desc: "Demandez votre devis gratuit. Nous intervenons à votre domicile pour le nettoyage de vitres, le ménage ou l'entretien.",
              },
              {
                icon: Wallet,
                title: '2. Vous payez moitié prix',
                desc: "Soit en avance immédiate Urssaf (vous ne payez que 50 %), soit la totalité avec récupération en déclaration d'impôts.",
              },
              {
                icon: BadgePercent,
                title: '3. L\'État rembourse',
                desc: "L'État vous rend les 50 % via votre déclaration d'impôts (ou directement par l'Urssaf si avance immédiate). Imposable ou pas.",
              },
            ].map((step, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-md border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-4">
                  <step.icon className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          {/* Concrete example */}
          <div className="mt-12 bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-3xl p-8">
            <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wider mb-3">Exemple concret</p>
            <div className="grid sm:grid-cols-3 gap-6 items-center">
              <div>
                <p className="text-sm text-gray-500 mb-1">Devis SMT</p>
                <p className="text-3xl font-bold text-gray-900">180 €</p>
              </div>
              <div className="text-center">
                <ArrowRight className="w-8 h-8 text-emerald-500 mx-auto rotate-90 sm:rotate-0" />
                <p className="text-xs text-gray-500 mt-1">- 50 % crédit d'impôt</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Coût réel pour vous</p>
                <p className="text-3xl font-bold text-emerald-600">90 €</p>
              </div>
            </div>
          </div>
        </section>

        {/* Conditions */}
        <section className="bg-white py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">
              Conditions pour en bénéficier
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                'Être un particulier domicilié fiscalement en France',
                "L'intervention doit avoir lieu à votre domicile (principal ou secondaire)",
                "Plafond annuel de 12 000 €/foyer fiscal (6 000 € de crédit d'impôt max)",
                'Majoration de 1 500 € par enfant à charge ou personne de +65 ans (plafond global jusqu\'à 15 000 €)',
                "Accessible même si vous n'êtes pas imposable (vrai crédit d'impôt, pas une simple réduction)",
                "Aucun engagement minimum — intervention ponctuelle ou régulière acceptée",
              ].map((cond, i) => (
                <div key={i} className="flex gap-3 items-start bg-slate-50 rounded-xl p-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700 leading-relaxed">{cond}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Avantages particuliers */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">
            Pourquoi en profiter avec SMT Services ?
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { icon: Wallet, title: 'Vous économisez réellement', desc: "Sur un budget annuel de 600 € de ménage et nettoyage de vitres, vous récupérez 300 €. Sur 1 200 €/an, c'est 600 € qui reviennent dans votre poche." },
              { icon: Clock, title: 'Avance immédiate possible', desc: "Plus besoin d'avancer 100 % puis d'attendre la déclaration : grâce à Unipros et l'Urssaf, vous payez directement votre part de 50 % à chaque intervention." },
              { icon: Shield, title: 'Service certifié SAP', desc: "Notre adhésion à la coopérative Unipros nous donne accès à la certification Services à la Personne sans paperasse pour vous." },
              { icon: HomeIcon, title: 'Tout sous un seul prestataire', desc: "Nettoyage de vitres, vérandas, ménage, repassage, entretien canapé… une seule entreprise locale, un seul interlocuteur, un seul crédit d'impôt." },
              { icon: Sparkles, title: 'Confort et qualité de vie', desc: "Reprenez du temps libre : laissez-nous vous offrir un intérieur impeccable, sans culpabilité financière grâce à la moitié remboursée." },
              { icon: Users, title: 'Aussi pour les seniors', desc: "Majoration de plafond pour les +65 ans. Idéal pour maintenir un logement propre quand la mobilité diminue, avec un coût allégé." },
            ].map((adv, i) => (
              <div key={i} className="flex gap-4 bg-white rounded-2xl p-6 shadow-md border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[hsl(207,90%,54%)]/10 flex items-center justify-center flex-shrink-0">
                  <adv.icon className="w-6 h-6 text-[hsl(207,90%,44%)]" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-1.5">{adv.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{adv.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Unipros section */}
        <section className="bg-gradient-to-br from-slate-900 to-blue-950 text-white py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-orange-300 font-semibold text-sm uppercase tracking-wider mb-3">Notre partenaire coopératif</p>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-5 leading-tight">
                  SMT Services est adhérent <span className="text-orange-300">Unipros</span>
                </h2>
                <p className="text-slate-300 mb-4 leading-relaxed">
                  <strong className="text-white">Unipros</strong> est une coopérative française d'intérêt collectif (SCIC) qui regroupe plus de <strong className="text-white">7 000 professionnels</strong> du service à la personne et accompagne plus de <strong className="text-white">300 000 clients particuliers</strong>.
                </p>
                <p className="text-slate-300 mb-4 leading-relaxed">
                  Grâce à cette adhésion, nous bénéficions de la certification <strong className="text-white">SAP (Services à la Personne)</strong> officielle, ce qui nous permet de vous faire profiter automatiquement du <strong className="text-orange-300">crédit d'impôt de 50 %</strong> sur toutes nos prestations à domicile — sans démarche administrative compliquée pour vous.
                </p>
                <p className="text-slate-300 mb-6 leading-relaxed">
                  Unipros propose également <strong className="text-white">l'avance immédiate Urssaf</strong> : un dispositif qui vous évite d'avancer la totalité de la facture, vous payez directement votre part de 50 % à chaque intervention.
                </p>
                <a
                  href="https://unipros.coop"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-orange-300 font-semibold hover:text-orange-200 hover:underline"
                  data-testid="unipros-external-link"
                >
                  En savoir plus sur Unipros <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <div className="bg-white rounded-3xl p-8 sm:p-10 shadow-2xl">
                <img
                  src="/img/unipros-logo.png"
                  alt="Logo Unipros — coopérative française du service à la personne"
                  className="w-full h-auto max-w-sm mx-auto"
                  loading="lazy"
                  data-testid="unipros-logo"
                />
                <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-slate-900">7 000+</p>
                    <p className="text-xs text-slate-500 mt-1">Adhérents pros</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">300 K+</p>
                    <p className="text-xs text-slate-500 mt-1">Clients particuliers</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">0 %</p>
                    <p className="text-xs text-slate-500 mt-1">Commission</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">
            Questions fréquentes sur le crédit d'impôt
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            Tout ce que les particuliers de Royan et alentours nous demandent avant de réserver.
          </p>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, idx) => {
              const open = openIdx === idx;
              return (
                <div
                  key={idx}
                  className="bg-white hover:bg-slate-50/70 rounded-2xl border border-slate-200 overflow-hidden transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setOpenIdx(open ? -1 : idx)}
                    className="w-full text-left flex items-center justify-between gap-4 p-5 sm:p-6 cursor-pointer"
                    data-testid={`credit-faq-q-${idx}`}
                    aria-expanded={open}
                  >
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug">
                      {item.q}
                    </h3>
                    <ChevronDown className={`flex-shrink-0 w-5 h-5 text-[hsl(207,90%,54%)] transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                  </button>
                  <div className={`grid transition-all duration-300 ${open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                    <div className="overflow-hidden">
                      <p className="px-5 sm:px-6 pb-5 sm:pb-6 text-gray-700 text-sm sm:text-base leading-relaxed" data-testid={`credit-faq-a-${idx}`}>
                        {item.a}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-gradient-to-br from-[hsl(32,95%,55%)] to-[hsl(32,95%,45%)] py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <BadgePercent className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">Prêt à diviser votre facture par 2 ?</h2>
            <p className="opacity-95 mb-7 max-w-xl mx-auto">
              Demandez votre devis gratuit en moins d'une minute. Nous vous indiquons directement le montant net après crédit d'impôt.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Button asChild size="lg" className="bg-white text-[hsl(32,95%,45%)] hover:bg-orange-50 font-bold">
                <Link to="/#reservation" data-testid="footer-cta-devis">
                  <FileText className="w-4 h-4 mr-2" /> Demander un devis gratuit
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[hsl(32,95%,45%)] font-semibold">
                <a href="tel:+33625684849" data-testid="footer-cta-call">
                  <Phone className="w-4 h-4 mr-2" /> 06 25 68 48 49
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CreditImpotPage;
